import * as T from '../types';

/** What the item sheet's actions need: the two calls, the page's state and its snackbar. */
export type StockActionDeps = {
  adjustStock: (itemId: T.Guid, delta: number) => Promise<T.StockItem>;
  addShoppingNeed: (body: T.AddShoppingNeedRequest) => Promise<T.ShoppingNeed>;
  replace: (item: T.StockItem) => void;
  notify: (message: string, options?: { actionText?: string; action?: () => void; severity?: 'success' | 'error' | 'info' | 'warning' }) => void;
  /** "Passo a prever?": opens the prediction dialog. */
  predict: (prediction: T.ShoppingPrediction) => void;
};

/** "Pôr na lista": whole packages when bought by the package, else how much is bought at a time (as on Android). */
export function stockToBuy(item: T.StockItem): { quantity: number | null; unit: T.MeasureUnit | null } {
  const pack = item.packSize;
  if (pack !== null && pack !== undefined && pack > 0) {
    return { quantity: Math.max(1, Math.ceil((item.reorderQuantity ?? pack) / pack)), unit: 'Package' };
  }
  return { quantity: item.reorderQuantity ?? null, unit: null };
}

/** "Acabou": down to nothing, with "Desfazer" to put back what there was. */
export async function markOut(item: T.StockItem, deps: StockActionDeps): Promise<void> {
  const had = item.quantity ?? 0;
  if (had <= 0) return;
  try {
    deps.replace(await deps.adjustStock(item.catalogItemPublicId, -had));
  } catch (e: any) {
    deps.notify(e.message, { severity: 'error' });
    return;
  }
  deps.notify(`${item.name}: acabou`, {
    actionText: 'Desfazer',
    action: async () => {
      try {
        deps.replace(await deps.adjustStock(item.catalogItemPublicId, had));
      } catch (e: any) {
        deps.notify(e.message, { severity: 'error' });
      }
    },
  });
}

/** "Pôr na lista": the usual amount onto the shopping list; asks "Passo a prever?" when the app did not see it coming. */
export async function putOnList(item: T.StockItem, deps: StockActionDeps): Promise<void> {
  const { quantity, unit } = stockToBuy(item);
  let need: T.ShoppingNeed;
  try {
    need = await deps.addShoppingNeed({ catalogItemPublicId: item.catalogItemPublicId, quantity, unit });
  } catch (e: any) {
    deps.notify(e.message, { severity: 'error' });
    return;
  }
  deps.replace({ ...item, isOnList: true });
  const prediction = need.prediction;
  if (prediction) {
    deps.notify(`${item.name} juntado à lista. Passo a prever?`, { actionText: 'Prever', action: () => deps.predict(prediction) });
  } else {
    deps.notify(`${item.name} juntado à lista`, { severity: 'success' });
  }
}
